import { useEffect, useMemo, useState } from 'react';
import { BlockStack, Card, DataTable, Divider, InlineGrid, Modal, Tabs, Text } from '@shopify/polaris';

export type PreviewInvoiceLineItem = {
  STT?: number;
  THHDVu?: string;
  SLuong?: number;
  DVTinh?: string;
  DGia?: number;
  ThTien?: number;
  TSuat?: number;
  TThue?: number;
  TgTien?: number;
  MHHDVu?: string;
};

export type PreviewInvoiceData = {
  taxType?: string;
  KHMSHDon?: string;
  KHHDon?: string;
  NLap?: string;
  HTTToan?: string;
  MTChieu?: string;

  NMua_Ten?: string;
  NMua_HVTNMHang?: string;
  NMua_SDThoai?: string;
  NMua_MST?: string;
  NMua_CCCDan?: string;
  NMua_DChi?: string; // Buyer Address
  NMua_MKHang?: string;
  NMua_DCTDTu?: string; // Address

  TgThTien?: number;
  TgTThue?: number;
  TgTTTBSo?: number;
  DSHHDVu?: PreviewInvoiceLineItem[];
};

type AccountingInvoicePreviewModalProps = {
  open: boolean;
  data: PreviewInvoiceData[] | null;
  loading?: boolean;
  onClose: () => void;
  onCreateInvoice: () => void;
};

export default function AccountingInvoicePreviewModal({ open, data, loading, onClose, onCreateInvoice }: AccountingInvoicePreviewModalProps) {
  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(0);
  const invoiceData = useMemo(() => data || [], [data]);
  const selectedInvoice = invoiceData[selectedInvoiceTab] || invoiceData[0] || null;

  useEffect(() => {
    if (!open) return;
    setSelectedInvoiceTab(0);
  }, [open, invoiceData.length]);

  const tabs = useMemo(
    () =>
      invoiceData.map((invoice, index) => ({
        id: `invoice-${index}`,
        content: `Đơn ${index + 1} /${invoice.taxType}`,
      })),
    [invoiceData]
  );

  const summaryItems = useMemo(
    () => [
      { label: 'LOẠI HÓA ĐƠN', value: selectedInvoice?.taxType || '' },
      { label: 'Mẫu số/Ký hiệu', value: selectedInvoice?.KHMSHDon ? `${selectedInvoice.KHMSHDon} - ${selectedInvoice.KHHDon || '-'}` : '-' },
      { label: 'Ngày lập', value: selectedInvoice?.NLap || '-' },
      { label: 'Hình thức thanh toán', value: selectedInvoice?.HTTToan || '-' },
      { label: 'Mã tham chiếu', value: selectedInvoice?.MTChieu || '-' },
    ],
    [selectedInvoice]
  );
  const buyerInformation = useMemo(
    () => [
      { label: 'Tên người mua', value: selectedInvoice?.NMua_HVTNMHang || '-' },
      { label: 'Tên đơn vị', value: selectedInvoice?.NMua_Ten || '-' },
      { label: 'Mã số thuế', value: selectedInvoice?.NMua_MST || '-' },
      { label: 'Số căn cước công dân', value: selectedInvoice?.NMua_CCCDan || '-' },
      { label: 'Địa chỉ', value: selectedInvoice?.NMua_DChi || '-' },
      { label: 'Số điện thoại', value: selectedInvoice?.NMua_SDThoai || '-' },
      { label: 'Địa chỉ nhận hóa đơn', value: selectedInvoice?.NMua_DCTDTu || '-' },
    ],
    [selectedInvoice]
  );
  const payInformation = useMemo(
    () => [
      ['Tổng tiền trước thuế', selectedInvoice?.TgThTien?.toLocaleString('vi-VN') || '-'],
      ['Tổng thuế', selectedInvoice?.TgTThue?.toLocaleString('vi-VN') || '-'],
      ['Tổng thanh toán', selectedInvoice?.TgTTTBSo?.toLocaleString('vi-VN') || '-'],
    ],
    [selectedInvoice]
  );
  const lineItemRows = useMemo(
    () =>
      selectedInvoice?.DSHHDVu?.map((line) => [
        line.STT?.toString() || '-',
        line.THHDVu || '-',
        line.SLuong?.toString() || '-',
        line.DVTinh || '-',
        line.DGia?.toLocaleString('vi-VN') || '-',
        line.TSuat !== undefined ? `${line.TSuat}%` : '-',
        line.ThTien?.toLocaleString('vi-VN') || '-',
        line.TThue?.toLocaleString('vi-VN') || '-',
        line.TgTien?.toLocaleString('vi-VN') || '-',
      ]) || [],
    [selectedInvoice?.DSHHDVu]
  );

  const infoRows = (items: { label: string; value: string }[]) => (
    <BlockStack gap="200">
      {items.map((item) => (
        <InlineGrid key={item.label} columns={{ xs: '1fr', sm: '1fr 2fr' }} gap="200">
          <Text as="span" variant="bodySm" fontWeight="semibold">
            {item.label}
          </Text>
          <Text as="span" variant="bodySm">
            {item.value}
          </Text>
        </InlineGrid>
      ))}
    </BlockStack>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Xem trước hóa đơn"
      size="large"
      primaryAction={{
        content: 'Tạo hóa đơn',
        onAction: onCreateInvoice,
        loading,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: onClose,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {tabs.length ? <Tabs tabs={tabs} selected={selectedInvoiceTab} onSelect={setSelectedInvoiceTab} /> : null}
          <Card padding="400">
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Thông tin chung
              </Text>
              {infoRows(summaryItems)}
            </BlockStack>
          </Card>
          <Card padding="400">
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Thông tin nguời mua
              </Text>
              {infoRows(buyerInformation)}
            </BlockStack>
          </Card>
          <Card padding="400">
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Thông tin thanh toán
              </Text>
              <DataTable hideScrollIndicator columnContentTypes={['text', 'numeric']} headings={['Khoản mục', 'Số tiền']} rows={payInformation} />
            </BlockStack>
          </Card>
          <Divider />
          <Text as="h3" variant="headingMd">
            Danh sách hàng hóa/dịch vụ
          </Text>
          <BlockStack gap="300">
            {selectedInvoice?.DSHHDVu?.length ? (
              <Card padding="400">
                <DataTable
                  verticalAlign="middle"
                  hideScrollIndicator
                  hasZebraStripingOnData
                  columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                  headings={['STT', 'Hàng hóa/Dịch vụ', 'Số lượng', 'ĐVT', 'Đơn giá', 'Thuế suất', 'Tiền hàng', 'Thuế', 'Tổng']}
                  rows={lineItemRows}
                />
              </Card>
            ) : (
              <Text as="span" variant="bodySm">
                Chưa có dữ liệu hàng hóa/dịch vụ.
              </Text>
            )}
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

